import { useCallback, useEffect, useMemo, useState } from "react";
import { registerSW } from "virtual:pwa-register";

// The service worker precaches the whole app, so after a deploy the browser
// keeps serving the bundle it already has until the page is reloaded. That is
// what made the first load after every deploy show the previous version.
//
// vite-plugin-pwa's `autoUpdate` reloads by itself, and here that is the wrong
// cure: the image, the erase rectangles and the traced SVG live in React state
// only -- nothing is written to storage -- so reloading mid-session throws away
// work the user can only get back by re-uploading and paying for another AI
// clean-up. So the reload is taken over instead: App takes it immediately when
// nothing is loaded, and otherwise waits behind a banner.
//
// In development `registerSW` is a no-op stub, so none of this runs there.

// Registration is per document, not per component: StrictMode runs effects
// twice, and a second registration would attach a second `activated` listener.
let registered = false;

/** `ready` turns true once a newer version has taken over and only a reload is missing. */
export function useAppUpdate(): { ready: boolean; reload: () => void } {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (registered) return;
    registered = true;
    registerSW({ immediate: true, onNeedReload: () => setReady(true) });
  }, []);

  const reload = useCallback(() => window.location.reload(), []);
  // Stable identity: callers put this in an effect's dependency list.
  return useMemo(() => ({ ready, reload }), [ready, reload]);
}
